/**
 * Chart Abstraction Action Menu Item
 * 
 * A self-contained menu item for the Actions dropdown in the Quality Summary table.
 * Handles adding/removing measures from the Chart Abstraction pending list.
 */

import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ClipboardList, Check } from "lucide-react";
import { useChartAbstraction } from "@/contexts/ChartAbstractionContext";

interface ChartAbstractionActionMenuItemProps {
  measureId: string;
}

export function ChartAbstractionActionMenuItem({ measureId }: ChartAbstractionActionMenuItemProps) {
  const { 
    hasQuestionsDocument, 
    hasMeasureQuestions, 
    isMeasureInList, 
    toggleMeasure 
  } = useChartAbstraction();

  // No questions document uploaded - show disabled with tooltip
  if (!hasQuestionsDocument) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative flex cursor-not-allowed select-none items-center rounded-sm px-2 py-1.5 text-sm text-muted-foreground opacity-50">
              <ClipboardList className="mr-2 h-4 w-4" />
              Add to Chart Abstraction List
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Upload a Chart Abstraction Questions document in Admin → System Settings</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Questions document exists but this measure has no questions defined
  if (!hasMeasureQuestions(measureId)) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative flex cursor-not-allowed select-none items-center rounded-sm px-2 py-1.5 text-sm text-muted-foreground opacity-50">
              <ClipboardList className="mr-2 h-4 w-4" />
              Add to Chart Abstraction List
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>No abstraction questions defined for this measure</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Measure has questions - show toggle option
  const isInList = isMeasureInList(measureId);

  return (
    <DropdownMenuItem onClick={() => toggleMeasure(measureId)}>
      {isInList ? (
        <>
          <Check className="mr-2 h-4 w-4 text-green-600" />
          Remove from Chart Abstraction List
        </>
      ) : (
        <>
          <ClipboardList className="mr-2 h-4 w-4" />
          Add to Chart Abstraction List
        </>
      )}
    </DropdownMenuItem>
  );
}





